import { useEffect, useRef, useState } from 'react';

/** メインタブ / メディア切替バーが横スクロールできる側にかけるフェードの幅(px) */
export const TAB_FADE_WIDTH = 32;

/**
 * 横スクロールできる側の端だけをフェードさせる `mask-image` を返す。
 *
 * MUI の矢印(TabScrollButton)は無効なときも幅を占有して端に死んだ余白を作るので使わない。
 * 続きがあることは端の透過で示す。**端に達している側はフェードさせない**
 * (常時ぼかしっぱなしにしない)。
 *
 * 監視対象はスクローラ本体と、その中身(= firstElementChild)の両方。
 * 前者は表示幅の変化、後者はラベルや件数が後から変わる場合(i18n の差し替え・
 * カウント取得)に効く。
 *
 * @param resolveScroller スクロールする要素を返す。ref でも querySelector でもよい
 * @param revision        これが変わったら対象を取り直す(タブ列の作り替え等)
 */
export function useEdgeFade(
  resolveScroller: () => HTMLElement | null | undefined,
  revision?: unknown,
): string {
  // resolve は毎レンダー新しい関数になるので deps には入れない。
  // 入れると毎レンダーで監視を張り直すことになる。
  const resolveRef = useRef(resolveScroller);
  resolveRef.current = resolveScroller;

  const [fade, setFade] = useState({ left: false, right: false });

  useEffect(() => {
    const scroller = resolveRef.current();
    if (!scroller) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;
      // 端の判定は小数のズレを拾わないよう1px余裕を持たせる
      const next = {
        left: scrollLeft > 1,
        right: scrollLeft + clientWidth < scrollWidth - 1,
      };
      // 同じ値で setState すると RO → render → RO の往復になる
      setFade(prev => (prev.left === next.left && prev.right === next.right ? prev : next));
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);

    return () => {
      scroller.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [revision]);

  return `linear-gradient(to right,
    transparent 0px,
    #000 ${fade.left ? TAB_FADE_WIDTH : 0}px,
    #000 calc(100% - ${fade.right ? TAB_FADE_WIDTH : 0}px),
    transparent 100%)`;
}
